import React, { useEffect, useState, useContext } from 'react';
import { Table, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { ActionButtons } from '../../common';
import { theme } from '../../../theme';
import InsumoModal from './InsumoModal';

interface InsumoData {
  id: number;
  codbarra: string;
  descripcion: string;
  unidad: string;
  ubicacion: string;
  grupo: string;
  subgrupo: string;
  marca: string;
  alta: string;
}

/*let desired_output = (data: any, column: any) => {
  let unique_values = data
      .map((item: any) => item[column])
      .filter(
          (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
      );
  return unique_values;
};*/

// Definir la interfaz para el contexto
interface InsumoContextType {
  selectedInsumoId: number | null;
  setSelectedInsumoId: (id: number | null) => void;
}

// Crear el contexto con un valor inicial
const InsumoContext = React.createContext<InsumoContextType>({
  selectedInsumoId: null,
  setSelectedInsumoId: () => {}
});

// Definir el tipo para las props del Provider
type InsumoProviderProps = {
  children: React.ReactNode;
};

// Crear el provider
export const InsumoProvider: React.FC<InsumoProviderProps> = ({ children }) => {
  const [selectedInsumoId, setSelectedInsumoId] = useState<number | null>(null);
  
  return (
    <InsumoContext.Provider value={{ selectedInsumoId, setSelectedInsumoId }}>
      {children}
    </InsumoContext.Provider>
  );
};

const ListarInsumos: React.FC = () => {
  const {user} = useAuth0();
  const empresa = user?.id_empresa;
  const [data, setData] = useState<InsumoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [insumoEditar, setInsumoEditar] = useState<any>(null);
  const { getAccessTokenSilently ,isLoading} = useAuth0();
  const { setSelectedInsumoId } = useContext(InsumoContext);
/*  const getTipoText = (tipo: number) => {
    switch (tipo) {
      case 1:
        return 'Grupo';
      case 2:
        return 'Subgrupo';
      case 3:
        return 'Marca';
      default:
        return 'Desconocido';
    }
  };*/

  /*const clasificacionesUnicas = desired_output(data, 'clasificacion_texto')
  .map((clasificacion: any) => ({text: clasificacion, value: clasificacion}));*/
  const desired_output = (data: any, column: any) => {
    const unique_values = data
        .map((item: any) => item[column])
        .filter(
            (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
        );
    return unique_values;
  };
  
  const GrupoUnicos = desired_output(data, 'grupo')
  .map((grupo: any) => ({text: grupo, value: grupo}));

  const SubgrupoUnicos = desired_output(data, 'subgrupo')
  .map((subgrupo: any) => ({text: subgrupo, value: subgrupo}));

  const MarcaUnicos = desired_output(data, 'marca')
  .map((marca: any) => ({text: marca, value: marca}));

  /*const EstadoUnicos = desired_output(data, 'alta')
  .map((alta: any) => ({text: alta, value: alta}));*/


  const columns: ColumnsType<InsumoData> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Código de Barra</span>,
      dataIndex: 'codbarra',
      key: 'codbarra',
      width: 150,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Descripción</span>,
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Unidad</span>,
      dataIndex: 'unidad',
      key: 'unidad',
      width: 100,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Ubicación</span>,
      dataIndex: 'ubicacion',
      key: 'ubicacion',
      width: 120,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Grupo</span>,
      dataIndex: 'grupo',
      key: 'grupo',
      filters: GrupoUnicos,
      onFilter: (value: any, record: InsumoData) => record.grupo == value,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Subgrupo</span>,
      dataIndex: 'subgrupo',
      key: 'subgrupo',
      filters: SubgrupoUnicos,
      onFilter: (value: any, record: InsumoData) => record.subgrupo == value,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Marca</span>,
      dataIndex: 'marca',
      key: 'marca',
      filters: MarcaUnicos,
      onFilter: (value: any, record: InsumoData) => record.marca == value,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'alta',
      key: 'alta',
      align: 'center' as const,
      width: 120,
      filters: [
        { text: 'Activo', value: '1' },
        { text: 'Inactivo', value: '0' }
      ],
      onFilter: (value: any, record: InsumoData) => record.alta == value,
      render: (value: string) => {
        const isActivo = value === '1';
        return (
          <span style={{ 
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: isActivo ? '#d4edda' : '#f8d7da',
            color: isActivo ? '#155724' : '#721c24'
          }}>
            {isActivo ? 'Activo' : 'Inactivo'}
          </span>
        );
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      align: 'center' as const,
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: InsumoData) => (
        <ActionButtons
          onEdit={() => handleEditar(record)}
          onDelete={() => handleEliminar(record.id)}
          tooltips={{
            edit: 'Editar insumo',
            delete: 'Eliminar insumo'
          }}
        />
      ),
    }
  ];

  const fetchInsumos = async () => {
    setLoading(true);
    try {
      console.log('empresa',empresa)
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_insumos/${empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los insumos');
      }
      
      const result = await response.json();
      console.log('result',result);
      setData(result);
      console.log('result',result);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los insumos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      fetchInsumos();
    }
  }, [isLoading, empresa]);

  const handleNuevo = () => {
    setInsumoEditar(null);
    setModalVisible(true);
  };

  const handleEditar = (insumo: any) => {
    setInsumoEditar(insumo);
    setSelectedInsumoId(insumo.id);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setInsumoEditar(null);
  };

  const handleModalSuccess = () => {
    fetchInsumos(); // Recargar lista
  };

  const handleEliminar = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este insumo?')) {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/eliminar_insumo/${id}/${empresa}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Error al eliminar el insumo');
        }
        
        message.success('Insumo eliminado correctamente');
        fetchInsumos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        message.error('Error al eliminar el insumo');
      }
    }
  };

  return (
    <div>
      {/* Botón Nuevo arriba */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <Button 
          type='primary'
          onClick={handleNuevo}
          style={{
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
            fontWeight: 500
          }}
        >
          Nuevo Insumo
        </Button>
      </div>
      
      {/* Tabla con acciones inline */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          pageSize: 10,
          total: data.length,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} registros`,
        }}
      />

      {/* Modal para crear/editar */}
      <InsumoModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        insumo={insumoEditar}
      />
    </div>
  );
};

export default ListarInsumos; 